import 'moment/locale/es';
import moment from 'moment';
import { round2Decimal } from './utils';


const sortLevelsByFromDays = function(levels) {
  if (!levels || !Array.isArray(levels)) return [];
  return levels.slice().sort(function(a, b) {
    return a.from_days - b.from_days;
  });
};

export const hasEarlyPaymentUtil = function(invoice, options) {
  if (!invoice) return false;
  
  options = options || {};
  const skipDateValidation = options.skipDateValidation || false;
  const attributes = invoice.attributes;
  const earlyPayment = invoice.earlyPayment;

  // New system: earlyPayment object
  if (earlyPayment) {
    if (skipDateValidation) {
      return true;
    }

    // Tiered scheme - check levels array first (priority)
    const levels = earlyPayment.invoiceEarlyPaymentLevels || earlyPayment.earlyPaymentLevels || earlyPayment.levels;
    if (levels && Array.isArray(levels) && levels.length > 0) {
      if (invoice.issue_date) {
        const maxDays = Math.max.apply(null, levels.map(function(l) { return l.to_days; }));
        let issueDateMs;
        if (typeof invoice.issue_date === 'string') {
          issueDateMs = moment(invoice.issue_date).valueOf();
        } else {
          issueDateMs = invoice.issue_date > 9999999999 
            ? invoice.issue_date 
            : invoice.issue_date * 1000;
        }
        const issueDate = moment(issueDateMs);
        const limitDate = issueDate.clone().add(maxDays, 'days');
        const today = moment();
        return today.isSameOrBefore(limitDate, 'day');
      }
      return true;
    }

    // Linear scheme - fallback if no levels array
    if (earlyPayment.days) {
      if (invoice.issue_date) {
        let issueDateMs;
        if (typeof invoice.issue_date === 'string') {
          issueDateMs = moment(invoice.issue_date).valueOf();
        } else {
          issueDateMs = invoice.issue_date > 9999999999 
            ? invoice.issue_date 
            : invoice.issue_date * 1000;
        }
        const issueDate = moment(issueDateMs);
        const limitDate = issueDate.clone().add(earlyPayment.days, 'days');
        const today = moment();
        return today.isSameOrBefore(limitDate, 'day');
      }
      return true;
    }
  }

  // Legacy system: early_payment object (old format)
  if (Object.prototype.hasOwnProperty.call(invoice, 'early_payment')) {
    if (skipDateValidation) {
      return true;
    }
    
    if (invoice.early_payment) {
      const earlyPaymentDays = invoice.early_payment.early_payment_days;
      if (earlyPaymentDays && invoice.issue_date) {
        let issueDateMs;
        if (typeof invoice.issue_date === 'string') {
          issueDateMs = moment(invoice.issue_date).valueOf();
        } else {
          issueDateMs = invoice.issue_date > 9999999999 
            ? invoice.issue_date 
            : invoice.issue_date * 1000;
        }
        const issueDate = moment(issueDateMs);
        const limitDate = issueDate.clone().add(earlyPaymentDays, 'days');
        const today = moment();
        return today.isSameOrBefore(limitDate, 'day');
      }
      return true;
    }
  }

  // Legacy system: attributes
  if (skipDateValidation && attributes && Object.prototype.hasOwnProperty.call(attributes, 'early_payment_days')) {
    return true;
  }

  if (attributes && attributes.early_payment_days) {
    if (!invoice.issue_date) {
      if (attributes.early_payment_percent) {
        return true;
      }
      
      if (attributes.early_payment_type && attributes.early_payment_value) {
        return true;
      }
      
      return false;
    }

    // Calculate if early payment is still valid
    let issueDateMs;
    if (typeof invoice.issue_date === 'string') {
      issueDateMs = moment(invoice.issue_date).valueOf();
    } else {
      issueDateMs = invoice.issue_date > 9999999999 
        ? invoice.issue_date 
        : invoice.issue_date * 1000;
    }
    
    const issueDate = moment(issueDateMs);
    const limitDate = issueDate.clone().add(parseInt(attributes.early_payment_days), 'days');
    const today = moment();
    const isValidDate = today.isSameOrBefore(limitDate, 'day');
    
    if (attributes.early_payment_percent && isValidDate) {
      return true;
    }
    
    if (attributes.early_payment_type && attributes.early_payment_value && isValidDate) {
      return true;
    }
  }
  
  return false;
};

export const getApplicableEarlyPaymentLevel = function(invoice) {
  if (!invoice || !invoice.earlyPayment) return null;
  
  const earlyPayment = invoice.earlyPayment;
  const levelsRaw = earlyPayment.invoiceEarlyPaymentLevels || earlyPayment.earlyPaymentLevels || earlyPayment.levels;
  
  // Check levels array first (priority)
  if (!levelsRaw || !Array.isArray(levelsRaw) || levelsRaw.length === 0) {
    return null;
  }

  // Sort levels by from_days to ensure correct order
  const levels = sortLevelsByFromDays(levelsRaw);

  if (!invoice.issue_date) return null;

  // Calculate days since issue
  let issueDateMs;
  if (typeof invoice.issue_date === 'string') {
    issueDateMs = moment(invoice.issue_date).valueOf();
  } else {
    issueDateMs = invoice.issue_date > 9999999999 
      ? invoice.issue_date 
      : invoice.issue_date * 1000;
  }
  const issueDate = moment(issueDateMs);
  const today = moment();
  const daysSinceIssue = today.diff(issueDate, 'days');

  for (let i = 0; i < levels.length; i++) {
    const level = levels[i];
    if (daysSinceIssue >= level.from_days && daysSinceIssue <= level.to_days) {
      return level;
    }
  }

  return null;
};

export const getDiscountTotalUtil = function(invoice, onlyDiscount) {
  if (!invoice) return 0;
  
  onlyDiscount = onlyDiscount || false;
  let discount = 0;
  const balance = invoice.balance || 0;

  // New system: earlyPayment object
  if (invoice.earlyPayment) {
    const earlyPayment = invoice.earlyPayment;
    
    // Tiered scheme - check levels array first (priority)
    const levels = earlyPayment.invoiceEarlyPaymentLevels || earlyPayment.earlyPaymentLevels || earlyPayment.levels;
    if (levels && Array.isArray(levels) && levels.length > 0) {
      const applicableLevel = getApplicableEarlyPaymentLevel(invoice);
      if (applicableLevel) {
        if (earlyPayment.type === 'fixed_amount') {
          discount = parseFloat(applicableLevel.value);
        } else {
          discount = balance * (parseFloat(applicableLevel.value) / 100);
        }
      }
    }
    // Linear scheme - fallback if no levels array
    else if (earlyPayment.value) {
      if (earlyPayment.type === 'fixed_amount') {
        discount = parseFloat(earlyPayment.value);
      } else {
        discount = balance * (parseFloat(earlyPayment.value) / 100);
      }
    }
  }
  // Legacy system: early_payment object (old format)
  else if (Object.prototype.hasOwnProperty.call(invoice, 'early_payment')) {
    if (invoice.early_payment.early_payment_type === 'fixed_amount') {
      discount = parseFloat(invoice.early_payment.early_payment_value);
    } else {
      discount = balance * (parseFloat(invoice.early_payment.early_payment_value || invoice.early_payment.early_payment_percent) / 100);
    }
  }
  // Legacy system: attributes
  else if (invoice.attributes && invoice.attributes.early_payment_percent) {
    discount = balance * (parseFloat(invoice.attributes.early_payment_percent) / 100);
  }
  else if (invoice.attributes && invoice.attributes.early_payment_type && invoice.attributes.early_payment_value) {
    if (invoice.attributes.early_payment_type === 'fixed_amount') {
      discount = parseFloat(invoice.attributes.early_payment_value);
    } else {
      discount = balance * (parseFloat(invoice.attributes.early_payment_value) / 100);
    }
  }

  const roundedDiscount = round2Decimal(discount);

  if (invoice.interest_moratorium && Object.keys(invoice.interest_moratorium).length > 0) {
    return onlyDiscount ? roundedDiscount : invoice.interest_moratorium.calcs.amount_debt - roundedDiscount;
  } else {
    return onlyDiscount ? roundedDiscount : balance - roundedDiscount;
  }
};

export const getEarlyPaymentMaxDays = function(invoice) {
  if (!invoice) return null;

  // New system: earlyPayment object
  if (invoice.earlyPayment) {
    const earlyPayment = invoice.earlyPayment;
    
    // Tiered scheme - check levels array first (priority)
    const levels = earlyPayment.invoiceEarlyPaymentLevels || earlyPayment.earlyPaymentLevels || earlyPayment.levels;
    if (levels && Array.isArray(levels) && levels.length > 0) {
      return Math.max.apply(null, levels.map(function(l) { return l.to_days; }));
    }
    
    // Linear scheme - fallback if no levels array
    if (earlyPayment.days) {
      return earlyPayment.days;
    }
  }

  // Legacy system: early_payment object
  if (invoice.early_payment && invoice.early_payment.early_payment_days) {
    return invoice.early_payment.early_payment_days;
  }

  // Legacy system: attributes
  if (invoice.attributes && invoice.attributes.early_payment_days) {
    return invoice.attributes.early_payment_days;
  }

  return null;
};

export const getEarlyPaymentApplicableDays = function(invoice) {
  if (!invoice) return null;

  if (invoice.earlyPayment) {
    const earlyPayment = invoice.earlyPayment;
    
    const levels = earlyPayment.invoiceEarlyPaymentLevels || earlyPayment.earlyPaymentLevels || earlyPayment.levels;
    if (levels && Array.isArray(levels) && levels.length > 0) {
      const applicableLevel = getApplicableEarlyPaymentLevel(invoice);
      if (applicableLevel) {
        return applicableLevel.to_days;
      }
      const sortedLevels = sortLevelsByFromDays(levels);
      return sortedLevels[0].to_days;
    }
    
    if (earlyPayment.days) {
      return earlyPayment.days;
    }
  }

  if (invoice.early_payment && invoice.early_payment.early_payment_days) {
    return invoice.early_payment.early_payment_days;
  }

  if (invoice.attributes && invoice.attributes.early_payment_days) {
    return invoice.attributes.early_payment_days;
  }

  return null;
};

/**
 * Gets a formatted discount text for display
 * @param {Object} invoice - The invoice object
 * @returns {string|null} - The formatted discount text or null
 */
export const getEarlyPaymentDiscountText = function(invoice) {
  if (!invoice) return null;

  const currency = invoice.currency || 'MXN';

  // New system: earlyPayment object
  if (invoice.earlyPayment) {
    const earlyPayment = invoice.earlyPayment;
    
    // Tiered scheme - check levels array first (priority)
    const levelsRaw = earlyPayment.invoiceEarlyPaymentLevels || earlyPayment.earlyPaymentLevels || earlyPayment.levels;
    if (levelsRaw && Array.isArray(levelsRaw) && levelsRaw.length > 0) {
      const applicableLevel = getApplicableEarlyPaymentLevel(invoice);
      if (applicableLevel) {
        if (earlyPayment.type === 'fixed_amount') {
          return 'Descuento de $' + applicableLevel.value + ' ' + currency;
        } else {
          return 'Descuento del ' + applicableLevel.value + '%';
        }
      }
      
      // Fallback: if no applicable level found, show the first level (earliest/highest discount)
      const levels = sortLevelsByFromDays(levelsRaw);
      const firstLevel = levels[0];
      if (earlyPayment.type === 'fixed_amount') {
        return 'Descuento de $' + firstLevel.value + ' ' + currency;
      } else {
        return 'Descuento del ' + firstLevel.value + '%';
      }
    }
    
    // Linear scheme - fallback if no levels array
    if (earlyPayment.value) {
      if (earlyPayment.type === 'fixed_amount') {
        return 'Descuento de $' + earlyPayment.value + ' ' + currency;
      } else {
        return 'Descuento del ' + earlyPayment.value + '%';
      }
    }
  }

  // Legacy system: early_payment object
  if (invoice.early_payment) {
    if (invoice.early_payment.early_payment_type === 'fixed_amount') {
      return 'Descuento de $' + invoice.early_payment.early_payment_value + ' ' + currency;
    } else if (invoice.early_payment.early_payment_value) {
      return 'Descuento del ' + invoice.early_payment.early_payment_value + '%';
    } else if (invoice.early_payment.early_payment_percent) {
      return 'Descuento del ' + invoice.early_payment.early_payment_percent + '%';
    }
  }

  // Legacy system: attributes
  if (invoice.attributes) {
    if (invoice.attributes.early_payment_percent) {
      return 'Descuento del ' + invoice.attributes.early_payment_percent + '%';
    }
    if (invoice.attributes.early_payment_type && invoice.attributes.early_payment_value) {
      if (invoice.attributes.early_payment_type === 'fixed_amount') {
        return 'Descuento de $' + invoice.attributes.early_payment_value + ' ' + currency;
      } else {
        return 'Descuento del ' + invoice.attributes.early_payment_value + '%';
      }
    }
  }

  return null;
};

export const getAppliedEarlyPaymentText = function(invoice) {
  if (!invoice || invoice.status_id !== 5) return null;
  if (!hasEarlyPaymentUtil(invoice, { skipDateValidation: true })) return null;
  
  const transactions = invoice.transaction || [];
  if (transactions.length === 0) return null;
  
  const totalPaid = transactions.reduce((sum, t) => {
    const invoiceTransactions = t.invoice_transaction || [];
    const transactionTotal = invoiceTransactions.reduce((tSum, it) => {
      return tSum + (Number(it.amount) || 0);
    }, 0);
    return sum + transactionTotal;
  }, 0);
  
  const invoiceTotal = Number(invoice.total) || 0;
  
  if (totalPaid < invoiceTotal && totalPaid > 0) {
    const actualDiscount = invoiceTotal - totalPaid;
    const currency = invoice.currency || 'MXN';
    const discountText = 'descuento de $' + actualDiscount.toFixed(2) + ' ' + currency;
    
    return 'El importe pagado refleja el descuento aplicado por pronto pago: ' + discountText;
  }
  
  return null;
};

export const validationEarlyPayment = hasEarlyPaymentUtil;


// WEBPACK FOOTER //
// ./src/libs/early-payment-utils.js